import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  CloudUpload,
  PhotoCamera,
  Mail,
  Phone,
  Person,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  // createTheme,
  styled,
  // useMediaQuery,
  // IconButtonProps,
  Paper,
} from "@mui/material";
import { useUpdateUser } from "../../hooks/query/useUser";
import { toast } from "react-toastify";

interface PersonalInfoFormProps {
  personalInfoData?: {
    id?: string;
    name?: string;
    email?: string;
    phoneNumber?: string;
    profileImageUrl?: string;
    signatureImageUrl?: string;
  };
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  personalInfoData,
}) => {
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | undefined>(
    personalInfoData?.profileImageUrl
  );
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | undefined>(
    personalInfoData?.signatureImageUrl
  );

  // const theme = createTheme({
  //   palette: {
  //     primary: {
  //       main: "#bb0000",
  //       light: "#e63434",
  //       dark: "#880000",
  //     },
  //     background: {
  //       default: "#f4f4f4",
  //     },
  //   },
  // });

  // const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: personalInfoData?.name || "",
      email: personalInfoData?.email || "",
      phoneNumber: personalInfoData?.phoneNumber || "",
    },
  });

  const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 150,
    height: 150,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
    },
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    position: "relative",
    transition: "transform 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.05)",
    },
  }));

  const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
    background: "linear-gradient(to bottom right, #ffffff, #f8f8f8)",
  }));

  const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.spacing(1),
      transition: "transform 0.2s",
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
  }));

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSignatureUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setSignatureFile(file);
      setSignaturePreview(URL.createObjectURL(file));
    }
  };

  const { mutateAsync: updateUser } = useUpdateUser();

  const onSubmit = async (data: {
    name: string;
    email: string;
    phoneNumber: string;
  }) => {
    toast.promise(
      new Promise((resolve, reject) => {
        try {
          updateUser(
            {
              userId: personalInfoData?.id,
              name: data.name,
              signature:
                signatureFile instanceof Blob ? signatureFile : undefined,
              photo: profileImage instanceof Blob ? profileImage : undefined,
            },
            {
              onSuccess: (response) => {
                console.log("User information updated successfully.");
                resolve(response);
              },
              onError: (error: any) => {
                const errorMessage =
                  error?.body?.error?.message ||
                  "User information failed to update.";
                console.error("Error message", errorMessage);
                reject(new Error(errorMessage));
              },
            }
          );
        } catch (error: any) {
          const errorMessage =
            error?.body?.error?.message ||
            "An unknown error occurred while updating user information.";
          console.error("Error message", errorMessage);
          reject(new Error(errorMessage));
        }
      }),
      {
        pending: "Updating user information...",
        success: "User information updated successfully! 🎉",
        error: {
          render({ data }: { data: any }) {
            console.error("Error data", data);
            // 'data' is the error object from the rejected promise
            return (
              data?.message ||
              "Failed to update user information. Please try again."
            );
          },
        },
      }
    );
  };

  return (
    <StyledPaper elevation={0}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ position: "relative" }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            background: "linear-gradient(45deg, #bb0000 30%, #e63434 90%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Personal Information
        </Typography>

        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box position="relative" sx={{ mb: 3 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                id="profile-image-upload"
                onChange={handleImageUpload}
              />
              <label htmlFor="profile-image-upload">
                <ProfileAvatar src={previewImage} alt="Profile Picture">
                  {personalInfoData?.name?.[0] || "P"}
                </ProfileAvatar>
                <IconButton
                  sx={{
                    position: "absolute",
                    bottom: 5,
                    right: 5,
                    backgroundColor: "primary.main",
                    color: "white",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      transform: "scale(1.1)",
                    },
                    transition: "transform 0.2s",
                  }}
                >
                  <PhotoCamera />
                </IconButton>
              </label>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "primary.light",
                backgroundColor: "rgba(187, 0, 0, 0.02)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                position: "relative",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "rgba(187, 0, 0, 0.05)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleSignatureUpload}
                style={{
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CloudUpload sx={{ fontSize: 48, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                >
                  Drop your signature here or click to upload
                </Typography>
              </Box>
            </Paper>

            {signaturePreview && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <img
                  src={signaturePreview}
                  alt="Signature Preview"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "100px",
                    display: "block",
                    margin: "0 auto",
                    objectFit: "contain",
                  }}
                />
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Name"
                  variant="outlined"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  InputProps={{
                    startAdornment: (
                      <Person sx={{ mr: 1, color: "primary.main" }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  disabled
                  InputProps={{
                    startAdornment: (
                      <Mail sx={{ mr: 1, color: "primary.main" }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  variant="outlined"
                  disabled
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ mr: 1, color: "primary.main" }} />
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(187, 0, 0, 0.2)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(187, 0, 0, 0.3)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </Box>
    </StyledPaper>
  );
};

export default PersonalInfoForm;
